import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GrowthStepResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { growthApi } from '@/services/growth.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

const QUERY_KEY = ['growth', 'steps'];

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/** The tenant's own discipleship path — an ordered catalog every person's
 *  growth progress picks from. The same archive/restore list pattern
 *  `AcademicLevelsPage` follows. */
export function GrowthStepsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const showArchived = getShowArchivedRecordsPreference();

  const steps = useQuery({
    queryKey: [...QUERY_KEY, showArchived],
    queryFn: () => growthApi.listSteps(showArchived),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: false });

  const list = steps.data ?? [];
  const canCreate = can('people.growthStep.create');

  return (
    <div className="page">
      <Link to="/people" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{t('people.growth.stepsTitle')}</h1>
          <p className="page-count">
            <Link to="/people/growth/report">{t('people.growth.viewReport')}</Link>
          </p>
        </div>
      </header>

      {canCreate && <CreateStepForm onCreated={() => void invalidate()} />}

      {steps.isPending && <Spinner label={t('common.states.loading')} />}
      {steps.isSuccess && list.length === 0 && <EmptyState title={t('people.growth.stepsEmpty')} />}

      {list.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.growth.stepName')}</th>
                <th scope="col">{t('people.growth.order')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {list.map((step) => (
                <StepRow key={step.id} step={step} onChanged={() => void invalidate()} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateStepForm({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [order, setOrder] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => growthApi.createStep({ name: name.trim(), order: Number(order) || 0 }),
    onSuccess: () => {
      setName('');
      setOrder('0');
      setError(null);
      onCreated();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  return (
    <form
      className="filters"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (name.trim()) create.mutate();
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <Field
        label={t('people.growth.stepName')}
        name="name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />
      <Field
        label={t('people.growth.order')}
        name="order"
        type="number"
        value={order}
        onChange={(event) => setOrder(event.target.value)}
        dir="ltr"
      />
      <Button type="submit" variant="primary" loading={create.isPending}>
        {t('people.growth.addStep')}
      </Button>
    </form>
  );
}

function StepRow({
  step,
  onChanged,
}: {
  readonly step: GrowthStepResponse;
  readonly onChanged: () => void;
}) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(step.name);
  const [order, setOrder] = useState(String(step.order));
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () =>
      growthApi.updateStep(step.id, { name: name.trim(), order: Number(order) || 0 }),
    onSuccess: () => {
      setError(null);
      setIsEditing(false);
      onChanged();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const archive = useMutation({
    mutationFn: () => growthApi.archiveStep(step.id),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const restore = useMutation({
    mutationFn: () => growthApi.restoreStep(step.id),
    onSuccess: onChanged,
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const canUpdate = can('people.growthStep.update');
  const isArchived = step.archivedAt !== null;

  if (isEditing) {
    return (
      <tr>
        <td colSpan={4}>
          {error && <Alert tone="danger">{error}</Alert>}
          <form
            className="filters"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              update.mutate();
            }}
          >
            <Field
              label={t('people.growth.stepName')}
              name={`edit-name-${step.id}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <Field
              label={t('people.growth.order')}
              name={`edit-order-${step.id}`}
              type="number"
              value={order}
              onChange={(event) => setOrder(event.target.value)}
              dir="ltr"
            />
            <Button type="submit" variant="primary" loading={update.isPending}>
              {t('common.actions.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>
              {t('common.actions.cancel')}
            </Button>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr data-archived={isArchived || undefined}>
      <td>{step.name}</td>
      <td dir="ltr">{step.order}</td>
      <td>{t(isArchived ? 'people.growth.archived' : 'people.growth.active')}</td>
      <td>
        {error && <Alert tone="danger">{error}</Alert>}
        {canUpdate && !isArchived && (
          <Button variant="ghost" onClick={() => setIsEditing(true)}>
            {t('common.actions.edit')}
          </Button>
        )}
        {canUpdate && !isArchived && (
          <Button variant="ghost" loading={archive.isPending} onClick={() => archive.mutate()}>
            {t('common.actions.archive')}
          </Button>
        )}
        {canUpdate && isArchived && (
          <Button variant="ghost" loading={restore.isPending} onClick={() => restore.mutate()}>
            {t('common.actions.restore')}
          </Button>
        )}
      </td>
    </tr>
  );
}
